import React, { useState, useRef } from 'react';
import {
  Utensils, Car, Zap, Gamepad2, ShoppingBag, Heart,
  GraduationCap, PiggyBank, MoreHorizontal, Briefcase,
  Laptop, TrendingUp, DollarSign, Camera, Image,
  Loader2, Check, ChevronDown, RefreshCw, CalendarClock,
  Home, Plane, Music, Dumbbell, Dog, Gift, Coffee,
  Wifi, Phone, Shirt, Baby, Book, Tv, Palette,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBudget, CATEGORIES, INCOME_CATEGORIES } from '@/contexts/BudgetContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Utensils, Car, Zap, Gamepad2, ShoppingBag, Heart,
  GraduationCap, PiggyBank, MoreHorizontal, Briefcase,
  Laptop, TrendingUp, DollarSign,
  // Custom category icons
  Home, Plane, Music, Dumbbell, Dog, Gift, Coffee,
  Wifi, Phone, Shirt, Baby, Book, Tv, Palette,
};

export function AddTransactionDialog({ open, onOpenChange }) {
  const { addTransaction, addTransactionsBulk, reloadData, getUniqueCategories, customCategoriesMeta } = useBudget();
  const { authFetch } = useAuth();
  const [mode, setMode] = useState('home'); // 'home', 'manual', 'subscription', 'scan'
  const [type, setType] = useState('expense');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [showCategories, setShowCategories] = useState(false);
  
  // Subscription state
  const [subFrequency, setSubFrequency] = useState('monthly'); // 'weekly', 'monthly', 'annually'
  const [subName, setSubName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  // Get dynamic categories based on type
  const uniqueCategories = getUniqueCategories();
  const categoryList = type === 'income' ? uniqueCategories.income : uniqueCategories.expense;
  const categories = type === 'income' ? INCOME_CATEGORIES : CATEGORIES;
  
  // Get config for selected category - check custom categories meta first for custom categories
  const getSelectedConfig = () => {
    if (categories[category]) return categories[category];
    if (customCategoriesMeta[category]) {
      return { icon: customCategoriesMeta[category].icon, color: null, customColor: customCategoriesMeta[category].color };
    }
    return null;
  };
  const selectedConfig = getSelectedConfig();
  const IconComponent = selectedConfig ? (iconMap[selectedConfig.icon] || MoreHorizontal) : MoreHorizontal;

  function resetForm() {
    setAmount('');
    setCategory('');
    setDescription('');
    setShowCategories(false);
    setMode('home');
    setScanResult(null);
    setIsScanning(false);
    setSubFrequency('monthly');
    setSubName('');
  }

  function handleSubmit() {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Enter an amount');
      return;
    }
    if (!category) {
      toast.error('Select a category');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    addTransaction({ 
      type, 
      amount: parseFloat(amount), 
      category, 
      description, 
      date: today 
    });
    toast.success(type === 'income' ? 'Income added!' : 'Expense added!');
    resetForm();
    onOpenChange(false);
  }

  async function handleSubscriptionSubmit() {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Enter an amount');
      return;
    }
    if (!category) {
      toast.error('Select a category');
      return;
    }
    if (!subName) {
      toast.error('Enter subscription name');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Save to subscriptions API
      const response = await authFetch('/api/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: subName,
          amount: parseFloat(amount),
          frequency: subFrequency,
          category: category,
          next_billing_date: today
        })
      });
      
      if (response.ok) {
        const freqLabel = subFrequency === 'weekly' ? '/week' : subFrequency === 'monthly' ? '/month' : '/year';
        toast.success(`Subscription added!`, {
          description: `${subName} - $${parseFloat(amount).toFixed(2)}${freqLabel}`
        });
        
        // Reload transactions to show the new subscription expense
        await reloadData();
        
        resetForm();
        onOpenChange(false);
      } else {
        toast.error('Failed to add subscription');
      }
    } catch (error) {
      toast.error('Failed to add subscription');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    
    setIsScanning(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { 'Authorization': 'Bearer ' + token } : {};
      const res = await fetch(BACKEND_URL + '/api/analyze-screenshot', {
        method: 'POST',
        body: formData,
        headers: headers
      });
      const data = await res.json();
      if (data.items && data.items.length > 0) {
        setScanResult(data);
        toast.success('Found ' + data.items.length + ' items');
      } else {
        toast.error('No items found');
        setMode('home');
      }
    } catch (err) {
      toast.error('Scan failed');
      setMode('home');
    } finally {
      setIsScanning(false);
    }
  }

  async function handleAddScannedItems() {
    if (!scanResult?.items?.length) return;
    const today = new Date().toISOString().split('T')[0];
    const transactions = [];
    for (let i = 0; i < scanResult.items.length; i++) {
      const item = scanResult.items[i];
      transactions.push({
        type: 'expense',
        amount: item.amount,
        category: item.category,
        description: item.name,
        date: scanResult.date_detected || today
      });
    }
    await addTransactionsBulk(transactions);
    toast.success('Added ' + transactions.length + ' items');
    resetForm();
    onOpenChange(false);
  }

  // Build category options - using dynamic category list
  const categoryOptions = [];
  for (let i = 0; i < categoryList.length; i++) {
    const cat = categoryList[i];
    // Check both predefined categories and custom categories meta
    const predefinedConfig = categories[cat];
    const customConfig = customCategoriesMeta[cat];
    
    let iconName = 'MoreHorizontal';
    let bgColor = '#6b728015';
    let iconColor = '#6b7280';
    
    if (predefinedConfig) {
      iconName = predefinedConfig.icon;
      bgColor = predefinedConfig.color ? 'hsl(var(--' + predefinedConfig.color + ') / 0.15)' : '#6b728015';
      iconColor = predefinedConfig.color ? 'hsl(var(--' + predefinedConfig.color + '))' : '#6b7280';
    } else if (customConfig) {
      iconName = customConfig.icon || 'MoreHorizontal';
      bgColor = customConfig.color ? `${customConfig.color}15` : '#6b728015';
      iconColor = customConfig.color || '#6b7280';
    }
    
    const CatIcon = iconMap[iconName] || MoreHorizontal;
    categoryOptions.push(
      <button
        key={cat}
        onClick={function() { setCategory(cat); setShowCategories(false); }}
        className={cn(
          'flex items-center gap-3 w-full p-3 rounded-xl transition-all text-left',
          category === cat ? 'bg-primary/10' : 'hover:bg-muted'
        )}
      >
        <div 
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: bgColor }}
        >
          <CatIcon className="w-4 h-4" style={{ color: iconColor }} />
        </div>
        <span className="text-sm font-medium">{cat}</span>
      </button>
    );
  }

  // Scan results list
  const scanItems = [];
  if (scanResult?.items) {
    for (let i = 0; i < scanResult.items.length; i++) {
      const item = scanResult.items[i];
      scanItems.push(
        <div key={i} className="flex justify-between items-center py-2">
          <span className="text-sm truncate flex-1">{item.name}</span>
          <span className="text-sm font-semibold ml-2">${item.amount.toFixed(2)}</span>
        </div>
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={function(isOpen) {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-[92vw] sm:max-w-[380px] rounded-3xl p-0 gap-0 border-0 shadow-2xl">
        
        {/* Scan Results */}
        {scanResult && scanResult.items && scanResult.items.length > 0 ? (
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-4">Receipt Items</h3>
            <div className="divide-y divide-border max-h-[250px] overflow-y-auto">
              {scanItems}
            </div>
            <div className="flex justify-between items-center pt-3 mt-3 border-t font-semibold">
              <span>Total</span>
              <span className="text-lg">${scanResult.total_amount?.toFixed(2)}</span>
            </div>
            <div className="flex gap-2 mt-5">
              <Button 
                variant="outline" 
                className="flex-1 h-12 rounded-xl"
                onClick={function() { setScanResult(null); setMode('home'); }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 h-12 rounded-xl bg-primary"
                onClick={handleAddScannedItems}
              >
                Add All
              </Button>
            </div>
          </div>
        ) : mode === 'scan' ? (
          /* Scanning State */
          <div className="p-5">
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            
            {isScanning ? (
              <div className="py-12 text-center">
                <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-primary" />
                <p className="text-sm text-muted-foreground">Analyzing receipt...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={function() { cameraRef.current && cameraRef.current.click(); }}
                  className="w-full p-6 rounded-2xl bg-primary/5 hover:bg-primary/10 transition-all flex flex-col items-center"
                >
                  <Camera className="w-8 h-8 text-primary mb-2" />
                  <span className="font-medium">Take Photo</span>
                </button>
                <button
                  onClick={function() { fileRef.current && fileRef.current.click(); }}
                  className="w-full p-4 rounded-xl border border-dashed hover:bg-muted/50 transition-all flex items-center justify-center gap-2"
                >
                  <Image className="w-5 h-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Choose from gallery</span>
                </button>
                <button
                  onClick={function() { setMode('home'); }}
                  className="w-full p-3 text-sm text-muted-foreground hover:text-foreground"
                >
                  Back
                </button>
              </div>
            )}
          </div>
        ) : mode === 'subscription' ? (
          /* Subscription Mode */
          showCategories ? (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Select Category</h3>
                <button onClick={function() { setShowCategories(false); }} className="text-sm text-muted-foreground">Done</button>
              </div>
              <div className="max-h-[300px] overflow-y-auto space-y-1">{categoryOptions}</div>
            </div>
          ) : (
            <div className="p-5">
              <div className="flex items-center gap-2 mb-5">
                <RefreshCw className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Add Subscription</h3>
              </div>

              {/* Subscription Name */}
              <Input
                placeholder="Netflix, Spotify, Gym..."
                value={subName}
                onChange={function(e) { setSubName(e.target.value); }}
                className="h-12 rounded-xl bg-muted/50 border-0 mb-4 text-center font-medium"
              />

              {/* Amount */}
              <div className="text-center mb-4">
                <div className="inline-flex items-baseline">
                  <span className="text-2xl text-muted-foreground mr-1">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    value={amount}
                    onChange={function(e) { setAmount(e.target.value); }}
                    className="text-4xl font-bold bg-transparent outline-none w-[120px] text-center"
                  />
                </div>
              </div>

              {/* Frequency Selector */}
              <div className="flex gap-1 p-1 bg-muted rounded-xl mb-4">
                <button
                  onClick={function() { setSubFrequency('weekly'); }}
                  className={cn(
                    'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                    subFrequency === 'weekly' ? 'bg-card shadow-sm' : 'text-muted-foreground'
                  )}
                >
                  Weekly
                </button>
                <button
                  onClick={function() { setSubFrequency('monthly'); }}
                  className={cn(
                    'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                    subFrequency === 'monthly' ? 'bg-card shadow-sm' : 'text-muted-foreground'
                  )}
                >
                  Monthly
                </button>
                <button
                  onClick={function() { setSubFrequency('annually'); }}
                  className={cn(
                    'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                    subFrequency === 'annually' ? 'bg-card shadow-sm' : 'text-muted-foreground'
                  )}
                >
                  Yearly
                </button>
              </div>

              {/* Category */}
              <button
                onClick={function() { setShowCategories(true); }}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-muted/50 hover:bg-muted transition-all mb-4"
              >
                <div className="flex items-center gap-3">
                  {IconComponent && selectedConfig ? (
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: selectedConfig.color ? 'hsl(var(--' + selectedConfig.color + ') / 0.15)' : '#6b728015' }}
                    >
                      <IconComponent className="w-4 h-4" style={{ color: selectedConfig.color ? 'hsl(var(--' + selectedConfig.color + '))' : '#6b7280' }} />
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                  <span className={cn('text-sm', category ? 'font-medium' : 'text-muted-foreground')}>
                    {category || 'Select category'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>

              {/* Monthly cost info */}
              {amount && parseFloat(amount) > 0 && (
                <p className="text-xs text-center text-muted-foreground mb-4">
                  {subFrequency === 'weekly' && `~$${(parseFloat(amount) * 4.33).toFixed(0)}/month`}
                  {subFrequency === 'annually' && `~$${(parseFloat(amount) / 12).toFixed(0)}/month`}
                  {subFrequency === 'monthly' && `$${parseFloat(amount).toFixed(0)}/month`}
                </p>
              )}

              {/* Submit */}
              <Button
                onClick={handleSubscriptionSubmit}
                disabled={!amount || !category || !subName}
                className="w-full h-12 rounded-xl font-medium bg-primary"
              >
                Add Subscription
              </Button>

              <button
                onClick={function() { setMode('home'); setCategory(''); }}
                className="w-full p-3 mt-2 text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
            </div>
          )
        ) : mode === 'manual' ? (
          /* Manual Entry Mode */
          showCategories ? (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Select Category</h3>
                <button onClick={function() { setShowCategories(false); }} className="text-sm text-muted-foreground">Done</button>
              </div>
              <div className="max-h-[300px] overflow-y-auto space-y-1">{categoryOptions}</div>
            </div>
          ) : (
            <div className="p-5">
              {/* Type Toggle */}
              <div className="flex gap-1 p-1 bg-muted rounded-xl mb-6">
                <button
                  onClick={function() { setType('expense'); setCategory(''); }}
                  className={cn(
                    'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                    type === 'expense' ? 'bg-card shadow-sm' : 'text-muted-foreground'
                  )}
                >
                  Expense
                </button>
                <button
                  onClick={function() { setType('income'); setCategory(''); }}
                  className={cn(
                    'flex-1 py-2.5 rounded-lg text-sm font-medium transition-all',
                    type === 'income' ? 'bg-card shadow-sm' : 'text-muted-foreground'
                  )}
                >
                  Income
                </button>
              </div>

              {/* Amount */}
              <div className="text-center mb-6">
                <div className="inline-flex items-baseline">
                  <span className="text-3xl text-muted-foreground mr-1">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="0"
                    value={amount}
                    onChange={function(e) { setAmount(e.target.value); }}
                    className="text-5xl font-bold bg-transparent outline-none w-[150px] text-center"
                  />
                </div>
              </div>

              {/* Category */}
              <button
                onClick={function() { setShowCategories(true); }}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-muted/50 hover:bg-muted transition-all mb-3"
              >
                <div className="flex items-center gap-3">
                  {IconComponent && selectedConfig ? (
                    <div 
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: selectedConfig.color ? 'hsl(var(--' + selectedConfig.color + ') / 0.15)' : '#6b728015' }}
                    >
                      <IconComponent className="w-4 h-4" style={{ color: selectedConfig.color ? 'hsl(var(--' + selectedConfig.color + '))' : '#6b7280' }} />
                    </div>
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                  <span className={cn('text-sm', category ? 'font-medium' : 'text-muted-foreground')}>
                    {category || 'Select category'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>

              {/* Description */}
              <Input
                placeholder="Add note (optional)"
                value={description}
                onChange={function(e) { setDescription(e.target.value); }}
                className="h-11 rounded-xl bg-muted/50 border-0 mb-4"
              />

              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={!amount || !category}
                className={cn(
                  'w-full h-12 rounded-xl font-medium',
                  type === 'income' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-primary hover:bg-primary/90'
                )}
              >
                {type === 'income' ? 'Add Income' : 'Add Expense'}
              </Button>

              <button
                onClick={function() { setMode('home'); }}
                className="w-full p-3 mt-2 text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
            </div>
          )
        ) : (
          /* Home - Main Options */
          <div className="p-5 space-y-3">
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" />
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />

            {/* Take Photo */}
            <button
              onClick={function() { cameraRef.current && cameraRef.current.click(); setMode('scan'); }}
              className="w-full p-5 rounded-2xl bg-primary/5 hover:bg-primary/10 border-2 border-dashed border-primary/30 transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Camera className="w-6 h-6 text-primary" />
              </div>
              <div className="text-left">
                <p className="font-semibold">Scan Receipt</p>
                <p className="text-xs text-muted-foreground">Take photo or upload</p>
              </div>
            </button>

            {/* Add Subscription */}
            <button
              onClick={function() { setMode('subscription'); setType('expense'); }}
              className="w-full p-5 rounded-2xl hover:bg-muted/50 border border-border transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-violet-500/10 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-violet-500" />
              </div>
              <div className="text-left">
                <p className="font-semibold">Add Subscription</p>
                <p className="text-xs text-muted-foreground">Weekly, monthly, or yearly</p>
              </div>
            </button>

            {/* Add Manually */}
            <button
              onClick={function() { setMode('manual'); }}
              className="w-full p-5 rounded-2xl hover:bg-muted/50 border border-border transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-muted-foreground" />
              </div>
              <div className="text-left">
                <p className="font-semibold">Add Manually</p>
                <p className="text-xs text-muted-foreground">Enter expense or income</p>
              </div>
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
